import { CORE_MODEL_INFO, isModelAvailableOnRoute } from '../coreModel';
import type { CoreModel } from '../coreModel';
import { logger } from '../logger';

const serialLogger = logger.withComponent('client-side-serial');

export type { CoreModel };

export interface ModelDerivationResult {
  success: boolean;
  model?: CoreModel;
  confidence: 'high' | 'medium' | 'low';
  method: 'pattern_match' | 'fallback';
  reason?: string;
}


const VALID_SERIAL_REGEX = /^[A-Za-z0-9]+$/;
const MIN_SERIAL_LENGTH = 9;
const MAX_SERIAL_LENGTH = 30;

export function isValidSerialFormat(serial: string): boolean {
  if (!serial || typeof serial !== 'string') {
    return false;
  }

  const trimmed = serial.trim();
  const trimmedLength = trimmed.length;
  return trimmedLength >= MIN_SERIAL_LENGTH &&
         trimmedLength <= MAX_SERIAL_LENGTH &&
         VALID_SERIAL_REGEX.test(trimmed);
}

export function normalizeSerial(serial: string): string {
  return serial.trim().toLowerCase();
}

export function deriveModelFromSerial(serial: string): ModelDerivationResult {
  serialLogger.debug('Attempting model derivation', { serial });

  if (!isValidSerialFormat(serial)) {
    serialLogger.warn('Invalid serial format', { serial });
    return {
      success: false,
      confidence: 'low',
      method: 'fallback',
    };
  }

  const normalized = normalizeSerial(serial);
  // This derivation only runs on the v2 client-side flow, so only attempt patterns
  // for models actually offered there (e.g. excludes v1-only models like Lite).
  const patternOrder: CoreModel[] = (['Pro_AI', 'Elite_AI', 'Mobile_AI', 'Pro', 'Lite'] as CoreModel[])
    .filter((model) => isModelAvailableOnRoute(model, 'v2'));

  for (const model of patternOrder) {
    const pattern = CORE_MODEL_INFO[model].serialPattern;
    if (pattern.test(normalized)) {
      serialLogger.info('Model derived successfully', {
        serial: normalized,
        model,
        pattern: pattern.toString(),
      });

      return {
        success: true,
        model,
        confidence: 'high',
        method: 'pattern_match',
      };
    }
  }

  serialLogger.warn('No pattern matched serial', {
    serial: normalized,
    patternsAttempted: patternOrder.length,
  });

  return {
    success: false,
    confidence: 'low',
    method: 'fallback',
  };
}


export function deriveModelsForSerials(serials: string[]): Array<{
  serial: string;
  result: ModelDerivationResult;
}> {
  return serials.map(serial => ({
    serial,
    result: deriveModelFromSerial(serial),
  }));
}
